import React from 'react'
import PropTypes from 'prop-types'
import moment from 'moment'

/**
 * What the shop has sent you, and what is still coming.
 *
 * Modelled on the ADAS report's activity log rather than invented: a reverse-chronological
 * list of things that happened, each with its own timestamp and outcome. That is the pattern
 * this portal already uses for history, and clients already read it.
 *
 * THREE STATES, NOT TWO. `Reminder.status` is inverted — the sender Lambda sets it FALSE once
 * a message has gone — but a reminder whose moment has passed and which the Lambda has NOT
 * flipped is neither sent nor upcoming. Calling that "sent" would tell a client a text went
 * out that never did, so it gets its own state and its own wording.
 */

// Deliberately conservative: only http(s), and only up to the first whitespace. A greedy
// pattern turns a trailing full stop into part of the URL, and anything looser risks
// linkifying text a client did not intend as a link.
const URL_SOURCE = 'https?:\\/\\/[^\\s<]+[^\\s<.,:;"\')\\]}]'

/**
 * Reminder messages routinely carry a confirm link or a PDF link, and until now they rendered
 * as dead text a client had to retype. Split on URLs and make those halves anchors.
 *
 * A FRESH regex per call, and a separate non-global one for the test. `RegExp.test` on a
 * /g regex advances lastIndex between calls, so a shared instance silently skips every other
 * match — the links would appear and disappear depending on how many came before them.
 */
const linkify = text => {
  const splitter = new RegExp(`(${URL_SOURCE})`, 'g')
  const isUrl = new RegExp(`^${URL_SOURCE}$`)
  return String(text || '').split(splitter).map((part, i) => {
    if (!isUrl.test(part)) return part
    return (
      // eslint-disable-next-line react/no-array-index-key
      <a key={i} href={part} target='_blank' rel='noopener noreferrer'>
        {part}
      </a>
    )
  })
}

const STATE_COPY = {
  sent: { label: 'Sent', tone: 'sent' },
  overdue: { label: 'Was due', tone: 'overdue' },
  upcoming: { label: 'Scheduled', tone: 'upcoming' },
}

const ReminderHistory = ({ reminders, shop }) => {
  if (!reminders || !reminders.length) return null

  const phone = shop && shop.phone ? String(shop.phone).replace(/[^0-9+]/g, '') : ''
  const email = (shop && shop.email) || ''

  return (
    <div className='reminder-history'>
      <div className='reminder-history__head'>Message history</div>

      <ul className='reminder-history__list'>
        {reminders.map(reminder => {
          const copy = STATE_COPY[reminder.state] || STATE_COPY.upcoming
          const when = reminder.sendDate ? moment(reminder.sendDate) : null
          return (
            <li key={reminder._id} className={`reminder-history__row reminder-history__row--${copy.tone}`}>
              <div className='reminder-history__meta'>
                <span className='reminder-history__channel'>
                  {(reminder.alertType || 'text').toUpperCase()}
                </span>
                <span className='reminder-history__state'>{copy.label}</span>
                {when && (
                  <span className='reminder-history__when' title={when.format('LLLL')}>
                    {when.format('ddd D MMM, h:mm A')}
                  </span>
                )}
              </div>

              <div className='reminder-history__message'>{linkify(reminder.message)}</div>

              {/* The delivery outcome the sender writes and nothing has ever displayed. */}
              {reminder.activity && (
                <div className='reminder-history__activity'>{reminder.activity}</div>
              )}
            </li>
          )
        })}
      </ul>

      {(phone || email) && (
        <div className='reminder-history__contact'>
          <span className='reminder-history__contact-label'>
            {shop.name ? `Reply to ${shop.name}` : 'Reply to the shop'}
          </span>
          <div className='reminder-history__contact-actions'>
            {/* tel: and sms: are inert on a desktop browser and native on a phone, which is
                exactly the behaviour wanted — no user-agent sniffing required. */}
            {phone && <a className='btn btn-outline-primary' href={`tel:${phone}`}>Call</a>}
            {phone && <a className='btn btn-outline-primary' href={`sms:${phone}`}>Text</a>}
            {email && <a className='btn btn-outline-primary' href={`mailto:${email}`}>Email</a>}
          </div>
        </div>
      )}
    </div>
  )
}

ReminderHistory.propTypes = {
  reminders: PropTypes.arrayOf(PropTypes.object),
  shop: PropTypes.objectOf(PropTypes.any),
}

ReminderHistory.defaultProps = {
  reminders: [],
  shop: null,
}

export default ReminderHistory
